import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/success-stories.css';

/**
 * Success Stories Page
 * 
 * Showcases detailed student success stories, achievements, and testimonials
 * with filtering options by exam type and achievement level.
 */

interface Story {
  id: number;
  name: string;
  image: string;
  achievement: string;
  exam: string;
  rank: string;
  score: string;
  year: number;
  college: string;
  course: string;
  location: string;
  preparationTime: string;
  previousAttempts: number;
  improvement: string;
  quote: string;
  fullStory: string;
  keyTakeaways: string[];
  favoriteFeatures: string[];
  category: 'IIT-JEE' | 'NEET' | 'NDA' | 'FOUNDATION';
}

const SuccessStories: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('ALL');
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);

  const successStories: Story[] = [
    {
      id: 1,
      name: "Aarav Sharma",
      image: "/img/success-stories/student-1.jpg",
      achievement: "IIT Bombay - Computer Science & Engineering",
      exam: "JEE Advanced",
      rank: "AIR 142",
      score: "324/360",
      year: 2024,
      college: "IIT Bombay",
      course: "B.Tech Computer Science",
      location: "Mumbai, Maharashtra",
      preparationTime: "18 months",
      previousAttempts: 1,
      improvement: "+180 marks in 4 months",
      quote: "WiziAI's AI performance analysis was a game-changer! It identified my weak topics in Physics that I wasn't even aware of.",
      fullStory: "I started my JEE preparation in 11th standard but struggled with Physics, especially Electrodynamics and Modern Physics. After my first attempt where I scored 198 marks, I was demotivated. That's when I discovered WiziAI.\n\nThe AI-powered performance analysis pinpointed exactly where I was losing marks - not just topics, but specific concept areas within those topics. The personalized study plan redistributed my time allocation, giving more focus to high-weightage weak areas.\n\nThe voice-enabled AI tutor was incredible for solving doubts at 2 AM when I was studying. Within 4 months of focused preparation using WiziAI, my marks jumped from 198 to 324. The mock tests with instant AI analysis after each test helped me understand my mistakes immediately rather than waiting for coaching classes.\n\nThe biggest help was the confidence tracking feature - seeing my confidence levels improve in weak topics kept me motivated throughout. Today, I'm at IIT Bombay studying Computer Science, and I credit a significant part of my success to WiziAI's intelligent learning system.",
      keyTakeaways: [
        "AI performance analysis identified hidden weaknesses",
        "Personalized study plan optimized time allocation",
        "Voice AI tutor helped solve doubts instantly at any time",
        "Mock tests with immediate AI feedback improved learning speed",
        "Confidence tracking kept motivation high"
      ],
      favoriteFeatures: [
        "AI Performance Analysis",
        "Voice-Enabled Doubt Solving",
        "Personalized Study Plans",
        "Instant Mock Test Feedback"
      ],
      category: 'IIT-JEE'
    },
    {
      id: 2,
      name: "Priya Patel",
      image: "/img/success-stories/student-2.jpg",
      achievement: "AIIMS Delhi",
      exam: "NEET",
      rank: "AIR 95",
      score: "710/720",
      year: 2024,
      college: "AIIMS Delhi",
      course: "MBBS",
      location: "Delhi",
      preparationTime: "24 months",
      previousAttempts: 0,
      improvement: "Consistent 99.8%ile scorer",
      quote: "The voice-enabled AI tutor was incredible! I could ask questions while studying late at night without disturbing anyone.",
      fullStory: "As a NEET aspirant from a small town, I didn't have access to top-tier coaching centers. My parents enrolled me in a local coaching, but I knew I needed something more to compete at the national level.\n\nWiziAI became my virtual coaching center. The comprehensive question bank covering NCERT, previous year papers, and advanced questions gave me exposure to all difficulty levels. The AI analysis after each test was like having a personal mentor who knew exactly what I needed to work on.\n\nWhat really set WiziAI apart was the subject-wise and chapter-wise performance tracking. I could see my Biology improving week by week, while Physics needed more attention. The smart leaderboard kept me competitive, and I loved seeing my rank improve as I put in more effort.\n\nThe voice AI tutor was a blessing during late-night study sessions. Instead of noting down doubts for the next day's class, I could get instant clarification on concepts. The detailed explanations helped me understand rather than just memorize.\n\nToday, I'm at AIIMS Delhi, and I can confidently say that WiziAI bridged the gap between small-town resources and metropolitan coaching quality.",
      keyTakeaways: [
        "Comprehensive question bank covered all difficulty levels",
        "Subject-wise tracking showed clear improvement areas",
        "Voice AI tutor provided instant doubt resolution",
        "Leaderboard created healthy competitive environment",
        "Detailed explanations promoted understanding over memorization"
      ],
      favoriteFeatures: [
        "Comprehensive Question Bank",
        "Subject-wise Performance Tracking",
        "Voice AI Doubt Solver",
        "Smart Leaderboard"
      ],
      category: 'NEET'
    },
    {
      id: 3,
      name: "Arjun Singh",
      image: "/img/success-stories/student-3.jpg",
      achievement: "National Defence Academy",
      exam: "NDA",
      rank: "AIR 28",
      score: "456/900",
      year: 2024,
      college: "NDA Khadakwasla",
      course: "Army",
      location: "Pune, Maharashtra",
      preparationTime: "12 months",
      previousAttempts: 0,
      improvement: "First attempt success",
      quote: "WiziAI's structured approach and disciplined study plan aligned perfectly with the defense forces mindset I wanted to develop.",
      fullStory: "Preparing for NDA requires not just academic excellence but also physical fitness and personality development. Time management was crucial, and that's where WiziAI proved invaluable.\n\nThe personalized study plan created by WiziAI's AI took into account my morning PT sessions and evening SSB preparation time, creating an optimal academic schedule. The mathematics section of NDA was challenging, but WiziAI's topic-wise breakdown with difficulty progression helped me build concepts systematically.\n\nThe mock tests simulated actual exam conditions, and the time management insights after each test taught me which questions to attempt first and which to skip. The AI analysis pointed out that I was spending too much time on moderate-difficulty questions - a pattern I never noticed myself.\n\nGeneral Knowledge preparation was made easier with WiziAI's current affairs integration and previous year question patterns. The spaced repetition system ensured I retained what I learned.\n\nWhat impressed me most was the holistic progress tracking. I could see my improvement across Mathematics, General Knowledge, and English simultaneously. The confidence scoring feature helped me identify topics where I needed more practice versus topics where I was exam-ready.\n\nToday, I'm proud to be at NDA Khadakwasla, training to serve my nation, and I'm grateful to WiziAI for being a reliable companion in my preparation journey.",
      keyTakeaways: [
        "Personalized study plan accommodated physical training schedule",
        "Topic-wise breakdown with difficulty progression built strong foundation",
        "Time management insights improved exam strategy",
        "Spaced repetition system enhanced retention",
        "Holistic progress tracking across all subjects"
      ],
      favoriteFeatures: [
        "Personalized Time-Optimized Study Plans",
        "Time Management Analytics",
        "Spaced Repetition System",
        "Holistic Progress Tracking"
      ],
      category: 'NDA'
    },
    {
      id: 4,
      name: "Ananya Gupta",
      image: "/img/success-stories/student-4.jpg",
      achievement: "IIT Kharagpur - Mechanical Engineering",
      exam: "JEE Advanced",
      rank: "AIR 387",
      score: "289/360",
      year: 2024,
      college: "IIT Kharagpur",
      course: "B.Tech Mechanical Engineering",
      location: "Kharagpur, West Bengal",
      preparationTime: "20 months",
      previousAttempts: 0,
      improvement: "From 60%ile in Mains to JEE Advanced qualifier",
      quote: "WiziAI helped me transform from an average student to an IIT qualifier through consistent, data-driven preparation.",
      fullStory: "I was never a naturally brilliant student. In my JEE Mains first attempt, I barely crossed 60 percentile. My parents were worried, and honestly, so was I. But I refused to give up.\n\nWiziAI's diagnostic test revealed something crucial - I had conceptual gaps in basic Physics and Chemistry, not advanced topics. Most coaching classes focus on tough problems, but WiziAI's AI identified that I needed to strengthen fundamentals first.\n\nThe adaptive learning system was brilliant. As I improved in basic concepts, the difficulty level automatically increased, ensuring I was always challenged but never overwhelmed. The chapter-wise mini-tests helped me master one concept before moving to the next.\n\nThe AI performance report after each test was like having a personal coach. It showed me not just which questions I got wrong, but why I got them wrong - was it a concept gap, calculation error, or time pressure? This insight was invaluable.\n\nThe study plan feature helped me balance school exams with JEE preparation. WiziAI adjusted my plan during exam weeks, reducing load but maintaining continuity.\n\nIn my second Mains attempt, I scored 97.8 percentile, qualifying for Advanced. The mock tests for Advanced with AI analysis prepared me well, and I secured AIR 387. From 60 percentile to IIT Kharagpur - WiziAI made this journey possible through smart, personalized learning.",
      keyTakeaways: [
        "Diagnostic test identified fundamental concept gaps",
        "Adaptive learning system matched difficulty to skill level",
        "AI performance reports explained error patterns",
        "Flexible study plans balanced school and competitive exams",
        "Consistent practice with intelligent feedback drives improvement"
      ],
      favoriteFeatures: [
        "Adaptive Learning System",
        "Diagnostic Tests",
        "AI Performance Reports",
        "Flexible Study Plans"
      ],
      category: 'IIT-JEE'
    },
    {
      id: 5,
      name: "Rohan Mehta",
      image: "/img/success-stories/student-5.jpg",
      achievement: "MAMC Delhi - Medicine",
      exam: "NEET",
      rank: "AIR 452",
      score: "685/720",
      year: 2024,
      college: "Maulana Azad Medical College",
      course: "MBBS",
      location: "New Delhi",
      preparationTime: "15 months",
      previousAttempts: 1,
      improvement: "+95 marks from previous attempt",
      quote: "After a disappointing first attempt, WiziAI's scientific approach helped me analyze failures and convert them into success.",
      fullStory: "My first NEET attempt was heartbreaking - I scored 590, missing my dream college by a huge margin. I was confused about whether to drop a year or settle for a private college. My parents supported my decision to try once more.\n\nThis time, I approached preparation scientifically with WiziAI. The first thing I did was analyze my previous attempt using WiziAI's detailed error analysis tool. It categorized my mistakes into silly errors, concept gaps, and tough questions. Surprisingly, 40% of lost marks were due to silly mistakes!\n\nWiziAI's mock tests focused on reducing these silly errors through time-bound practice and stress simulation. The AI identified patterns - I was making most errors in questions 130-160, indicating concentration drop after 3 hours.\n\nThe study plan incorporated short breaks and attention refreshers. The spaced repetition ensured I didn't forget what I learned months ago. Biology, being memory-intensive, benefited hugely from this approach.\n\nThe confidence tracking was psychological magic. Seeing my confidence improve from 60% to 90% in weak chapters boosted my morale. The peer comparison feature showed me where I stood compared to other aspirants, keeping me grounded yet motivated.\n\nIn my second attempt, I scored 685, a jump of 95 marks. Today I'm at MAMC Delhi, one of India's premier medical colleges. WiziAI taught me that with the right tools and approach, improvement is always possible.",
      keyTakeaways: [
        "Error analysis categorized mistakes for targeted improvement",
        "Mock tests reduced silly errors through systematic practice",
        "Spaced repetition prevented knowledge decay",
        "Confidence tracking provided psychological boost",
        "Peer comparison maintained competitive edge"
      ],
      favoriteFeatures: [
        "Detailed Error Analysis",
        "Spaced Repetition System",
        "Confidence Tracking",
        "Peer Comparison Analytics"
      ],
      category: 'NEET'
    },
    {
      id: 6,
      name: "Ishaan Verma",
      image: "/img/success-stories/student-6.jpg",
      achievement: "Class 10 - 98.6% CBSE",
      exam: "CBSE Board",
      rank: "School Topper",
      score: "493/500",
      year: 2024,
      college: "Preparing for IIT-JEE",
      course: "Science (PCM + Computer)",
      location: "Bangalore, Karnataka",
      preparationTime: "10 months",
      previousAttempts: 0,
      improvement: "From 85% in Class 9 to 98.6% in Class 10",
      quote: "WiziAI helped me build strong foundations in Class 10 that are now helping me excel in my JEE preparation.",
      fullStory: "In Class 9, I was an average student scoring around 85%. My parents wanted me to aim for IIT, but my scores were concerning. They enrolled me in WiziAI for foundation building.\n\nWiziAI's foundation program was different from regular coaching. It focused on conceptual clarity and application rather than rote learning. The NCERT-based question bank ensured I mastered board exam patterns while the advanced questions prepared me for competitive exams.\n\nThe subject-wise performance tracking showed me real-time progress. Mathematics was my strong suit, but Science needed work. The AI recommended focusing on Physics and Chemistry fundamentals, which would help both in boards and future JEE preparation.\n\nThe best feature was the board exam predictor. After each mock test, it predicted my board exam score range, helping me gauge preparation levels. The previous year question bank covered 10 years of CBSE papers, ensuring I saw every question pattern.\n\nThe study plan balanced board preparation with foundation for JEE. I didn't feel overwhelmed; instead, I felt confident. The smart notes feature helped me create revision material from practice sessions.\n\nI scored 98.6% in Class 10 boards, becoming school topper. Now in Class 11, I'm continuing with WiziAI for JEE preparation, and the strong foundation is showing results - I'm already scoring 85+ percentile in JEE mock tests.",
      keyTakeaways: [
        "Foundation program built strong conceptual base",
        "Board exam predictor helped gauge preparation level",
        "NCERT-based questions ensured board exam readiness",
        "Simultaneous preparation for boards and competitive exams",
        "Strong Class 10 foundation accelerated Class 11-12 learning"
      ],
      favoriteFeatures: [
        "Foundation Program",
        "Board Exam Predictor",
        "NCERT Question Bank",
        "Smart Notes Feature"
      ],
      category: 'FOUNDATION'
    }
  ];

  const filters = [
    { id: 'ALL', label: 'All Stories', count: successStories.length },
    { id: 'IIT-JEE', label: 'IIT-JEE', count: successStories.filter(s => s.category === 'IIT-JEE').length },
    { id: 'NEET', label: 'NEET', count: successStories.filter(s => s.category === 'NEET').length },
    { id: 'NDA', label: 'NDA', count: successStories.filter(s => s.category === 'NDA').length },
    { id: 'FOUNDATION', label: 'Foundation', count: successStories.filter(s => s.category === 'FOUNDATION').length }
  ];

  const filteredStories = activeFilter === 'ALL' 
    ? successStories 
    : successStories.filter(story => story.category === activeFilter);

  const stats = [
    { icon: 'fas fa-graduation-cap', value: '50,000+', label: 'Success Stories' },
    { icon: 'fas fa-trophy', value: '98%', label: 'Success Rate' },
    { icon: 'fas fa-medal', value: '2,500+', label: 'Top 100 Ranks' },
    { icon: 'fas fa-star', value: '4.9/5', label: 'Student Rating' }
  ];

  return (
    <div className="success-stories-page">
      {/* Hero Section */}
      <div className="success-hero" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '100px 0 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="container position-relative">
          <div className="row align-items-center">
            <div className="col-lg-8 mx-auto text-center">
              <div className="d-inline-flex align-items-center px-4 py-2 mb-4 rounded-pill"
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                <i className="fas fa-award me-2"></i>
                <span className="fw-semibold">Real Students. Real Results.</span>
              </div>
              
              <h1 className="display-3 fw-bold mb-4">
                Inspiring Success Stories
              </h1>
              
              <p className="lead mb-5 fs-4" style={{ opacity: 0.95 }}>
                Discover how thousands of students achieved their dreams with WiziAI's 
                intelligent learning platform. Their success could be your story too.
              </p>

              {/* Stats Bar */}
              <div className="row g-4 mt-5">
                {stats.map((stat, index) => (
                  <div key={index} className="col-6 col-lg-3">
                    <div className="p-4 rounded-4" style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <i className={`${stat.icon} fa-2x mb-3 d-block`}></i>
                      <div className="fs-2 fw-bold mb-1">{stat.value}</div>
                      <div className="small" style={{ opacity: 0.9 }}>{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="container py-5">
        <div className="filter-section text-center mb-5">
          <h3 className="fw-bold mb-4 text-dark">Filter by Exam</h3>
          <div className="d-flex flex-wrap justify-content-center gap-3">
            {filters.map(filter => (
              <button
                key={filter.id}
                className={`btn btn-lg rounded-pill px-4 py-3 ${activeFilter === filter.id ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveFilter(filter.id)}
                style={{
                  transition: 'all 0.3s ease',
                  fontWeight: 600
                }}>
                {filter.label}
                <span className="badge bg-white text-primary ms-2">{filter.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Success Stories Grid */}
        <div className="row g-4">
          {filteredStories.map((story, index) => (
            <div key={story.id} className="col-lg-4 col-md-6">
              <div className="success-story-card h-100 rounded-4 overflow-hidden shadow-sm"
                style={{
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                onClick={() => setSelectedStory(story)}>
                
                {/* Student Image */}
                <div className="position-relative" style={{ height: '280px' }}>
                  <img 
                    src={story.image}
                    alt={story.name}
                    className="w-100 h-100"
                    style={{ objectFit: 'cover' }}
                  />
                  <div className="position-absolute top-0 end-0 m-3">
                    <span className="badge bg-primary px-3 py-2">
                      {story.exam}
                    </span>
                  </div>
                  <div className="position-absolute bottom-0 start-0 w-100 p-4"
                    style={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)'
                    }}>
                    <h4 className="text-white fw-bold mb-1">{story.name}</h4>
                    <p className="text-white mb-0 small" style={{ opacity: 0.9 }}>
                      <i className="fas fa-map-marker-alt me-1"></i>
                      {story.location}
                    </p>
                  </div>
                </div>

                {/* Story Content */}
                <div className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span className="badge bg-success px-3 py-2">
                      <i className="fas fa-trophy me-1"></i>
                      {story.rank}
                    </span>
                    <span className="badge bg-info px-3 py-2">
                      {story.year}
                    </span>
                  </div>

                  <h5 className="fw-bold mb-2 text-primary">{story.achievement}</h5>
                  <p className="text-muted mb-3">
                    <i className="fas fa-book-open me-2"></i>
                    {story.course}
                  </p>

                  <div className="bg-light p-3 rounded-3 mb-3">
                    <div className="small text-muted mb-1">Score</div>
                    <div className="fw-bold text-dark">{story.score}</div>
                  </div>

                  {story.improvement && (
                    <div className="alert alert-warning py-2 px-3 mb-3">
                      <i className="fas fa-chart-line me-2"></i>
                      <small className="fw-semibold">{story.improvement}</small>
                    </div>
                  )}

                  <p className="text-muted mb-4" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    "{story.quote}"
                  </p>

                  <button className="btn btn-outline-primary w-100 rounded-pill"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStory(story);
                    }}>
                    Read Full Story
                    <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section py-5 mt-5" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 mx-auto text-center text-white">
              <h2 className="display-5 fw-bold mb-4">
                Ready to Write Your Success Story?
              </h2>
              <p className="lead mb-5" style={{ opacity: 0.95 }}>
                Join 50,000+ students who transformed their preparation with WiziAI's 
                intelligent learning platform.
              </p>
              <div className="d-flex flex-wrap gap-3 justify-content-center">
                <Link to="/signup" className="btn btn-light btn-lg px-5 py-3 rounded-pill fw-semibold">
                  <i className="fas fa-rocket me-2"></i>
                  Start Free Trial
                </Link>
                <Link to="/pricing" className="btn btn-outline-light btn-lg px-5 py-3 rounded-pill fw-semibold">
                  <i className="fas fa-tag me-2"></i>
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Full Story */}
      {selectedStory && (
        <div className="modal show d-block" style={{
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(5px)'
        }}
        onClick={() => setSelectedStory(null)}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="modal-content rounded-4 overflow-hidden">
              {/* Modal Header with Image */}
              <div className="position-relative" style={{ height: '350px' }}>
                <img 
                  src={selectedStory.image}
                  alt={selectedStory.name}
                  className="w-100 h-100"
                  style={{ objectFit: 'cover' }}
                />
                <div className="position-absolute top-0 end-0 m-3">
                  <button 
                    className="btn btn-light rounded-circle"
                    style={{ width: '40px', height: '40px' }}
                    onClick={() => setSelectedStory(null)}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="position-absolute bottom-0 start-0 w-100 p-4"
                  style={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)'
                  }}>
                  <h2 className="text-white fw-bold mb-2">{selectedStory.name}</h2>
                  <h4 className="text-white mb-3" style={{ opacity: 0.95 }}>
                    {selectedStory.achievement}
                  </h4>
                  <div className="d-flex flex-wrap gap-2">
                    <span className="badge bg-success px-3 py-2">
                      <i className="fas fa-trophy me-1"></i>
                      {selectedStory.rank}
                    </span>
                    <span className="badge bg-primary px-3 py-2">
                      {selectedStory.exam}
                    </span>
                    <span className="badge bg-info px-3 py-2">
                      Score: {selectedStory.score}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-body p-5">
                {/* Quick Stats */}
                <div className="row g-3 mb-5">
                  <div className="col-md-3 col-6">
                    <div className="p-3 bg-light rounded-3 text-center">
                      <i className="fas fa-calendar text-primary fa-2x mb-2"></i>
                      <div className="small text-muted">Preparation Time</div>
                      <div className="fw-bold">{selectedStory.preparationTime}</div>
                    </div>
                  </div>
                  <div className="col-md-3 col-6">
                    <div className="p-3 bg-light rounded-3 text-center">
                      <i className="fas fa-redo text-warning fa-2x mb-2"></i>
                      <div className="small text-muted">Attempts</div>
                      <div className="fw-bold">{selectedStory.previousAttempts === 0 ? 'First' : selectedStory.previousAttempts}</div>
                    </div>
                  </div>
                  <div className="col-md-3 col-6">
                    <div className="p-3 bg-light rounded-3 text-center">
                      <i className="fas fa-chart-line text-success fa-2x mb-2"></i>
                      <div className="small text-muted">Improvement</div>
                      <div className="fw-bold small">{selectedStory.improvement}</div>
                    </div>
                  </div>
                  <div className="col-md-3 col-6">
                    <div className="p-3 bg-light rounded-3 text-center">
                      <i className="fas fa-map-marker-alt text-danger fa-2x mb-2"></i>
                      <div className="small text-muted">Location</div>
                      <div className="fw-bold small">{selectedStory.location}</div>
                    </div>
                  </div>
                </div>

                {/* Full Story */}
                <div className="mb-5">
                  <h4 className="fw-bold mb-4 text-primary">
                    <i className="fas fa-book-open me-2"></i>
                    The Journey
                  </h4>
                  {selectedStory.fullStory.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-muted mb-3 fs-5 lh-lg">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Key Takeaways */}
                <div className="mb-5 p-4 rounded-4" style={{
                  background: 'linear-gradient(135deg, #667eea15, #764ba215)'
                }}>
                  <h4 className="fw-bold mb-4 text-primary">
                    <i className="fas fa-lightbulb me-2"></i>
                    Key Takeaways
                  </h4>
                  <ul className="list-unstyled">
                    {selectedStory.keyTakeaways.map((takeaway, index) => (
                      <li key={index} className="d-flex align-items-start mb-3">
                        <div className="flex-shrink-0 me-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: '30px',
                              height: '30px',
                              background: '#667eea',
                              color: 'white'
                            }}>
                            <i className="fas fa-check" style={{ fontSize: '0.8rem' }}></i>
                          </div>
                        </div>
                        <span className="flex-grow-1 text-dark fs-5">{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Favorite Features */}
                <div className="mb-4">
                  <h4 className="fw-bold mb-4 text-primary">
                    <i className="fas fa-star me-2"></i>
                    Favorite WiziAI Features
                  </h4>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedStory.favoriteFeatures.map((feature, index) => (
                      <span key={index} className="badge bg-primary px-4 py-3 fs-6">
                        <i className="fas fa-check-circle me-2"></i>
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer bg-light">
                <Link 
                  to="/signup" 
                  className="btn btn-primary btn-lg px-5 rounded-pill"
                  onClick={() => setSelectedStory(null)}>
                  Start Your Success Story
                  <i className="fas fa-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuccessStories;
